import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { api } from '../api';
import { Banner, Card, Copyable } from '../components/ui';

interface Created {
  tenant: { id: string; slug: string; name: string };
  adminKey: string;
  manageUrl: string;
  portalUrl: string;
}

/** Public landing page: mint a tenant with no account, or get back into an existing one. */
export function HomePage() {
  const navigate = useNavigate();
  const [name, setName] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [created, setCreated] = useState<Created | null>(null);

  const create = async () => {
    setBusy(true);
    setError('');
    try {
      setCreated(await api.post<Created>('/api/public/tenants', { name: name.trim() || undefined }));
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Could not create a tenant');
    } finally {
      setBusy(false);
    }
  };

  if (created) {
    return (
      <div className="content" style={{ maxWidth: 720, margin: '0 auto', paddingTop: 40 }}>
        <Brand />
        <Banner kind="warning">
          <strong>Save your admin link now.</strong> It is the only way back into this tenant —
          there is no account and no email recovery. Anyone with the link controls the tenant.
        </Banner>

        <Card title={created.tenant.name} hint={`Tenant ${created.tenant.slug} is ready.`}>
          <Copyable label="Admin link — bookmark this" value={created.manageUrl} />
          <Copyable label="Admin key" value={created.adminKey} />
          <Copyable label="Public portal (safe to share)" value={created.portalUrl} />
          <div className="row" style={{ marginTop: 16 }}>
            <button className="primary" onClick={() => navigate(`/t/${created.tenant.slug}/manage`)}>
              Open my tenant
            </button>
            <span className="faint small">You are already signed in on this browser.</span>
          </div>
        </Card>
      </div>
    );
  }

  return (
    <div className="content" style={{ maxWidth: 720, margin: '0 auto', paddingTop: 40 }}>
      <Brand />

      <p className="page-sub" style={{ marginBottom: 24, fontSize: 14 }}>
        A scratch environment for testing SSO integrations. Create a tenant, point it at your
        identity provider, run a real login, and see exactly what came back — the decoded
        assertion or token, every validation check, and the raw protocol data.
      </p>

      <Card title="Create a test tenant" hint="No account, no email. Takes a second.">
        {error && <Banner kind="error">{error}</Banner>}
        <label className="field">
          <span>Name it (optional)</span>
          <input
            value={name} placeholder="My test tenant" maxLength={120}
            onChange={(e) => setName(e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Enter') void create(); }}
          />
        </label>
        <button className="primary" onClick={create} disabled={busy}>
          {busy ? 'Creating…' : 'Create a test tenant'}
        </button>
      </Card>

      <Card title="What you can test">
        <dl className="kv">
          <dt>SAML 2.0</dt>
          <dd style={{ fontFamily: 'var(--sans)' }}>
            Acts as a service provider. Import your IdP metadata, run SP-initiated SSO, and get the
            assertion validated: signature, audience, conditions, InResponseTo, destination.
          </dd>
          <dt>OAuth 2.0 / OIDC</dt>
          <dd style={{ fontFamily: 'var(--sans)' }}>
            Acts as a relying party. Authorization code with PKCE, discovery, id_token verified
            against your JWKS, UserInfo, refresh and client credentials.
          </dd>
          <dt>PingAccess</dt>
          <dd style={{ fontFamily: 'var(--sans)' }}>
            Acts as a protected application. Put its URL behind PingAccess and see the identity
            headers and mediated token it injects — and whether the token actually validates.
          </dd>
        </dl>
      </Card>

      <Card title="Already have a tenant?">
        <p className="hint">
          Open the admin link you bookmarked, or go to <code>/t/&lt;your-tenant&gt;/manage</code>
          {' '}and paste your admin key.
        </p>
      </Card>

      <p className="faint small" style={{ textAlign: 'center', marginTop: 30 }}>
        This is a public testing tool. Use test credentials, not production secrets —
        captured results contain real tokens and are readable by anyone with the result link.
        {' · '}
        <Link to="/login">Operator sign in</Link>
      </p>
    </div>
  );
}

function Brand() {
  return (
    <div className="brand" style={{ marginBottom: 20 }}>
      <span className="dot" />
      <span>
        IAM Testbench
        <small>SAML · OAuth · PingAccess</small>
      </span>
    </div>
  );
}
